import React, { useEffect, useState } from "react";
import { DealsPageLayout } from "../deals/dealsPageLayout/DealsPageLayout";
import { Organization, Person, PersonCreate } from "../../api/generated-api";
import { configureAuthHeader, configureBaseUrl } from "../../api/axiosConfig";
import { connect } from "react-redux";
import { Grid, Typography, useTheme } from "@mui/material";
import {
  adminAddNewPersonToOrganization,
  adminGetOrganization,
  adminGetOrganizationPeople,
} from "../../api/admin/organizations";
import Button from "../common/forms/button";
import { sendInvitation } from "../../api/persons/persons";
import { DKDialog } from "../common/DKDialog";
import { isEmpty } from "../../helpers/utilsTS";
import DKFormContainer from "../common/forms/formContainer/DKFormContainer";
import DKFormTextInput from "../common/widgets/formVariablesWidget/DKFormTextInput";

const AdminOrganizationDetailPage = (props) => {
  const token = props.token;
  const orgId = props.orgId;
  configureBaseUrl();
  configureAuthHeader(token);

  const theme = useTheme();

  const [organization, setOrganization] = useState<Organization>();
  const [organizationPeople, setOrganizationPeople] = useState<Person[]>([]);
  const [showNewPersonDialog, setShowNewPersonDialog] =
    useState<boolean>(false);
  const [newPersonData, setNewPersonData] = useState<PersonCreate>();
  const [emptyFields, setEmptyFields] = useState<Map<string, boolean>>(
    new Map<string, boolean>()
  );

  const checkAndSet = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const value = event.target.value;
    const updatedEmptyFields = new Map<string, boolean>(emptyFields);

    if (isEmpty(value)) {
      updatedEmptyFields.set(name, true);
    } else {
      updatedEmptyFields.delete(name);
    }

    setEmptyFields(updatedEmptyFields);

    setNewPersonData({
      ...newPersonData,
      [name]: event.target.value,
    });
  };

  const isNewPersonSaveButtonDisabled = () => {
    return (
      isEmpty(newPersonData?.email) ||
      isEmpty(newPersonData?.firstName) ||
      isEmpty(newPersonData?.lastName)
    );
  };

  const fetchAndSetOrgPeople = () => {
    adminGetOrganizationPeople(orgId, (fetchedPeople) => {
      setOrganizationPeople(fetchedPeople);
    });
  };

  useEffect(() => {
    adminGetOrganization(orgId, (fetchedOrg) => {
      setOrganization(fetchedOrg);
    });

    fetchAndSetOrgPeople();
  }, [orgId]);

  if (!organization) {
    return null;
  }

  return (
    <DealsPageLayout
      title={organization.name}
      actionButtons={[
        {
          label: "New User",
          dataTestId: "admin-add-user-button",
          primary: true,
          onClick: () => {
            setShowNewPersonDialog(true);
          },
        },
      ]}
    >
      {organizationPeople.map((person) => {
        return (
          <Grid
            key={person.id}
            container
            sx={{
              padding: "20px",
              border: `1px solid ${theme.palette.tertiary.light}`,
              marginBottom: "20px",
            }}
          >
            <Grid item xs={4}>
              <Typography variant={"h3"} data-testid="client-name">
                {person.firstName} {person.lastName}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant={"h3"} data-testid="client-name">
                <a href={`mailto:${person.email}`}>{person.email}</a>
              </Typography>
            </Grid>
            <Grid
              item
              xs={4}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              {!person.user && (
                <Button
                  type={"secondary"}
                  size={"medium"}
                  onClick={() => {
                    sendInvitation(person.id, (invitedPerson) => {
                      const index = organizationPeople.findIndex((p) => {
                        return p.id == person.id;
                      });

                      const newOrganizationPeople = [...organizationPeople];
                      newOrganizationPeople[index] = invitedPerson;
                      setOrganizationPeople(newOrganizationPeople);
                    });
                  }}
                >
                  invite
                </Button>
              )}
              {person.user && !person.user.verified && (
                <Button
                  type={"secondary"}
                  size={"medium"}
                  onClick={() => {
                    sendInvitation(person.id);
                  }}
                >
                  send reminder
                </Button>
              )}
            </Grid>
          </Grid>
        );
      })}

      <DKDialog
        open={showNewPersonDialog}
        title="New Person"
        contentText={`The person will be added to ${organization.name}.`}
        actionButtons={[
          {
            label: "Cancel",
            dataTestId: "cancel-new-person",
            onClick: () => {
              setShowNewPersonDialog(false);
            },
          },
          {
            label: "Save",
            primary: true,
            dataTestId: "save-new-person",
            disabled: isNewPersonSaveButtonDisabled(),
            onClick: () => {
              setShowNewPersonDialog(false);
              adminAddNewPersonToOrganization(orgId, newPersonData, () => {
                fetchAndSetOrgPeople();
              });
            },
          },
        ]}
      >
        <DKFormContainer>
          <DKFormTextInput
            required={true}
            name="email"
            customErrorLabel={
              emptyFields.get("email") ? "Email is required." : undefined
            }
            dataTestId={"new-person-email"}
            label="Email"
            value={undefined}
            onChange={(event) => {
              checkAndSet(event);
            }}
          />
          <DKFormTextInput
            required={true}
            name="firstName"
            customErrorLabel={
              emptyFields.get("firstName")
                ? "First Name is required."
                : undefined
            }
            dataTestId={"new-client-firstName"}
            label="First Name"
            value={undefined}
            onChange={(event) => {
              checkAndSet(event);
            }}
          />
          <DKFormTextInput
            required={true}
            name="lastName"
            customErrorLabel={
              emptyFields.get("lastName") ? "Last Name is required." : undefined
            }
            dataTestId={"new-client-lastName"}
            label="Last Name"
            value={undefined}
            onChange={(event) => {
              checkAndSet(event);
            }}
          />
        </DKFormContainer>
      </DKDialog>
    </DealsPageLayout>
  );
};

const mapStateToProps = (state, props) => {
  const {
    match: {
      params: { orgId },
    },
  } = props;
  const { token } = state.auth;
  return {
    token,
    orgId,
  };
};

export default connect(mapStateToProps)(AdminOrganizationDetailPage);
